import { motion } from "framer-motion";

/* Premium golden "badge" graphics for each pillar — each ~140px round emblem */
const MAROON = "#C8961F";
const MAROON_LIGHT = "#E7B23A";
const CREAM = "#FFF7E2";

function BadgeFrame({ children, label, id }) {
  return (
    <div className="relative w-[160px] h-[160px]">
      <svg viewBox="0 0 180 180" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id={`bgGrad-${id}`} cx="50%" cy="40%" r="70%">
            <stop offset="0%" stopColor={CREAM} />
            <stop offset="100%" stopColor="#F1E7D5" />
          </radialGradient>
          <path id={`labelArc-${id}`} d="M 22 105 A 68 68 0 0 0 158 105" />
        </defs>
        <circle cx="90" cy="90" r="86" fill={`url(#bgGrad-${id})`} stroke={MAROON} strokeWidth="2" />
        <circle cx="90" cy="90" r="79" fill="none" stroke={MAROON} strokeWidth="0.6" strokeDasharray="2 4" />
        <circle cx="90" cy="90" r="68" fill="none" stroke={MAROON} strokeWidth="1" />
        {/* tick marks around */}
        {Array.from({ length: 28 }).map((_, i) => {
          const a = (i / 28) * Math.PI * 2;
          const r1 = 81;
          const r2 = 85;
          const x1 = 90 + Math.cos(a) * r1;
          const y1 = 90 + Math.sin(a) * r1;
          const x2 = 90 + Math.cos(a) * r2;
          const y2 = 90 + Math.sin(a) * r2;
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={MAROON} strokeWidth="0.7" opacity="0.5" />
          );
        })}
        {/* curved label at bottom */}
        <text fontSize="9" fontWeight="800" letterSpacing="2.5" fill={MAROON} fontFamily="Manrope, sans-serif">
          <textPath href={`#labelArc-${id}`} startOffset="50%" textAnchor="middle">{label}</textPath>
        </text>
        {/* small accent stars on sides */}
        <circle cx="22" cy="90" r="2" fill={MAROON} />
        <circle cx="158" cy="90" r="2" fill={MAROON} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[92px] h-[92px] mt-[-14px]">{children}</div>
      </div>
    </div>
  );
}

function Handshake() {
  // detailed handshake — two arms meeting, with cuffs
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <g fill="none" stroke={MAROON} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        {/* left forearm */}
        <path d="M6 64 L34 56" strokeWidth="6" stroke={MAROON_LIGHT} opacity="0.35" />
        <path d="M6 64 L34 56" />
        {/* left cuff */}
        <path d="M8 70 L4 60 L12 54 L16 64 Z" fill={MAROON} fillOpacity="0.18" />
        {/* right forearm */}
        <path d="M94 64 L66 56" strokeWidth="6" stroke={MAROON_LIGHT} opacity="0.35" />
        <path d="M94 64 L66 56" />
        {/* right cuff */}
        <path d="M92 70 L96 60 L88 54 L84 64 Z" fill={MAROON} fillOpacity="0.18" />
        {/* clasping hands - palms */}
        <path d="M34 56 C40 50, 48 50, 52 54 C56 50, 64 50, 66 56" />
        <path d="M34 56 C40 60, 48 62, 52 60 C56 62, 64 60, 66 56" fill={MAROON} fillOpacity="0.1" />
        {/* fingers (top hand) */}
        <path d="M40 52 L42 46" />
        <path d="M46 50 L48 44" />
        <path d="M52 50 L54 44" />
        <path d="M58 50 L60 46" />
        {/* thumb */}
        <path d="M50 54 L46 58" />
        {/* unity star at top */}
        <path d="M50 22 L52 30 L60 30 L54 35 L56 43 L50 38 L44 43 L46 35 L40 30 L48 30 Z" fill={MAROON} fillOpacity="0.85" stroke="none" />
      </g>
    </svg>
  );
}

function GlobeGraphic() {
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <g fill="none" stroke={MAROON} strokeWidth="2.2" strokeLinecap="round">
        <circle cx="50" cy="50" r="32" fill={MAROON} fillOpacity="0.08" />
        {/* continents abstracted */}
        <path d="M30 38 C36 32, 42 34, 44 40 C48 44, 54 42, 56 46 C60 50, 56 56, 50 56 C44 58, 38 54, 36 50 C32 48, 28 44, 30 38 Z" fill={MAROON} fillOpacity="0.55" stroke="none" />
        <path d="M58 60 C62 58, 68 60, 70 64 C72 68, 68 72, 64 70 C60 70, 58 64, 58 60 Z" fill={MAROON} fillOpacity="0.55" stroke="none" />
        {/* longitude / latitude */}
        <ellipse cx="50" cy="50" rx="32" ry="12" />
        <ellipse cx="50" cy="50" rx="12" ry="32" />
        <ellipse cx="50" cy="50" rx="24" ry="32" />
        <circle cx="50" cy="50" r="32" />
        {/* pin India */}
        <path d="M60 38 C60 34, 64 34, 64 38 C64 42, 62 44, 62 44 C62 44, 60 42, 60 38 Z" fill={MAROON} stroke="none" />
        {/* arrow loop globe */}
        <path d="M14 50 Q22 28 50 22" strokeDasharray="2 3" opacity="0.7" />
        <path d="M46 22 L50 22 L50 26" />
      </g>
    </svg>
  );
}

function Strength() {
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <g fill="none" stroke={MAROON} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        {/* dumbbell bar */}
        <rect x="12" y="46" width="76" height="8" rx="2" fill={MAROON} fillOpacity="0.15" />
        {/* weight plates left */}
        <rect x="6" y="34" width="10" height="32" rx="2" fill={MAROON} fillOpacity="0.55" stroke="none" />
        <rect x="2" y="38" width="6" height="24" rx="1" fill={MAROON} fillOpacity="0.85" stroke="none" />
        {/* weight plates right */}
        <rect x="84" y="34" width="10" height="32" rx="2" fill={MAROON} fillOpacity="0.55" stroke="none" />
        <rect x="92" y="38" width="6" height="24" rx="1" fill={MAROON} fillOpacity="0.85" stroke="none" />
        {/* outline */}
        <rect x="12" y="46" width="76" height="8" rx="2" />
        <rect x="6" y="34" width="10" height="32" rx="2" />
        <rect x="84" y="34" width="10" height="32" rx="2" />
        {/* shine / power lines */}
        <path d="M30 28 L36 22" />
        <path d="M50 24 L50 16" />
        <path d="M70 28 L64 22" />
      </g>
    </svg>
  );
}

function Consistency() {
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <g fill="none" stroke={MAROON} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        {/* equal repeating squares */}
        {[
          [14, 60], [34, 60], [54, 60], [74, 60],
        ].map(([x, y], idx) => (
          <g key={idx}>
            <rect x={x} y={y} width="12" height="12" fill={MAROON} fillOpacity={0.18} />
            <rect x={x} y={y} width="12" height="12" />
            <path d={`M${x + 3} ${y + 6} L${x + 6} ${y + 9} L${x + 10} ${y + 4}`} strokeWidth="2.4" />
          </g>
        ))}
        {/* horizontal baseline */}
        <line x1="8" y1="80" x2="92" y2="80" opacity="0.4" />
        {/* repeating pattern dots above */}
        {[20, 40, 60, 80].map((x) => (
          <circle key={x} cx={x} cy="32" r="3" fill={MAROON} stroke="none" />
        ))}
        <path d="M14 32 L86 32" strokeDasharray="3 4" opacity="0.5" />
        {/* equal sign emblem */}
        <line x1="40" y1="20" x2="60" y2="20" strokeWidth="3" />
        <line x1="40" y1="14" x2="60" y2="14" strokeWidth="3" />
      </g>
    </svg>
  );
}

const ITEMS = [
  {
    letter: "T",
    word: "Trust",
    label: "TRUST",
    body: "Trust is the bedrock of every Tirupati Global Sourcing Company (TGSC) relationship — earned, not assumed. From the first drawing review to final dispatch, OEMs across USA, UK, Europe and the Middle East rely on transparent processes, honest timelines and accountable delivery, every single batch.",
    Graphic: Handshake,
  },
  {
    letter: "G",
    word: "Global",
    label: "GLOBAL",
    body: "Global in reach yet rooted in India, TGSC collapses continents into a single accountable supply chain. One engineer, one purchase order, one consolidated shipment — quietly bridging USA, UK, Europe and the Middle East from a single point of contact.",
    Graphic: GlobeGraphic,
  },
  {
    letter: "S",
    word: "Strength",
    label: "STRENGTH",
    body: "Strength comes from depth. Group factories — Tirupati Engineering Works, Tirupati Autocomp Industrees and Tirupati Coters — handle forging, machining, welding, plating and paint in-house, while a vetted partner network extends our reach into casting and moulding without ever splitting your supply chain.",
    Graphic: Strength,
  },
  {
    letter: "C",
    word: "Consistent",
    label: "CONSISTENT",
    body: "Consistent quality is engineered, not promised on a brochure. Calibrated equipment, certified processes and a refusal to accept variance — disciplined to ISO 9001:2015, IATF 16949:2016 and VDA 6.3 — keep every batch you receive identical to the one before.",
    Graphic: Consistency,
  },
];

export default function Acronym() {
  return (
    <section
      id="tgsc"
      data-testid="tgsc-section"
      className="relative py-20 lg:py-24 bg-white overflow-hidden"
    >
      <div className="absolute inset-0 bp-grid-dark opacity-25 pointer-events-none" />

      <div className="container-x relative">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-brand-orange" />
            <span className="eyebrow">What TGSC stands for</span>
            <span className="h-px w-10 bg-brand-orange" />
          </div>
          <h2 className="h-section">
            Four letters.
            <br />
            <span className="text-brand-blue">One promise to every OEM.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
          {ITEMS.map((it, i) => {
            const G = it.Graphic;
            return (
              <motion.div
                key={it.letter}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`tgsc-${it.letter.toLowerCase()}`}
                className="bg-white p-7 lg:p-8 group hover:bg-brand-soft transition-colors flex flex-col"
              >
                <div className="flex items-baseline gap-3">
                  <div
                    className="font-heading font-extrabold leading-none"
                    style={{ color: MAROON, fontSize: "76px" }}
                  >
                    {it.letter}
                  </div>
                  <div className="text-xs uppercase tracking-[0.22em] text-brand-orange font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="mt-2 font-heading font-extrabold text-2xl text-brand-navy">
                  {it.word}
                </div>
                <p className="mt-4 text-sm text-brand-text/70 leading-relaxed">
                  {it.body}
                </p>
                <div className="mt-auto pt-6 flex items-center justify-center">
                  <BadgeFrame label={it.label} id={it.letter}>
                    <G />
                  </BadgeFrame>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
